// Added By Farhan Khan Khan on 24-Dec-2025
// Extended By System on 29-Jan-2026 - Added Mounting Structure BOM view support
// Fixed By System on 06-Feb-2026 - Added tabs for Standard and Mounting Structure line items
import React, { useState, useEffect } from "react";
import { Badge, Button, Col, Container, Row, Spinner, Table, Tabs, Tab } from "react-bootstrap";
import Confirm from "../Confirm";
import { Link, useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import solisPowerApi from "../../api/solisPowerApi";
import Helmet from "react-helmet";
import Main from "../layout/Main";
import { formatDateForView, getUserDetails } from "../../utils/helper";
import PERMISSIONS from "../../constants/permissions";
import {
  DatatableWrapper,
  Filter,
  Pagination,
  PaginationOptions,
  TableBody,
  TableHeader,
} from "react-bs-datatable";
import PubSub from "pubsub-js";
import InfoPill from "../InfoPill";
import MountingStructureView from "./MountingStructureView";

const BomView = (props) => {
  const userInfo = getUserDetails();
  const location = useLocation();
  const navigate = useNavigate();
  const [bom, setBom] = useState(location.state ? location.state : {});
  const [standardItems, setStandardItems] = useState([]);
  const [mountingStructureItems, setMountingStructureItems] = useState([]);
  const [modalShow, setModalShow] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('standard'); // Fixed By System on 06-Feb-2026 - Tab state

  useEffect(() => {
    fetchBom();
  }, []);

  const fetchBom = () => {
    if (!bom.id && location.hasOwnProperty("pathname") && location.pathname.split("/").length >= 3) {
      bom.id = location.pathname.split("/")[2];
    }

    async function initBom() {
      try {
        setLoading(true);
        let result = await solisPowerApi.fetchBomCategoryWithItems(bom.id);

        if (result) {
          setBom(result);

          // Fixed By System on 06-Feb-2026 - Separate STANDARD and MOUNTING_STRUCTURE items
          const bomType = result.bom_type || 'STANDARD';
          const allItems = result.items || [];

          // Filter STANDARD items (from bom_master_items table)
          const standardItemsList = allItems.filter(item =>
            !item.item_type ||
            item.item_type === 'STANDARD' ||
            (!item.roof_type && !item.component_name && item.product_id)
          );

          // For MOUNTING_STRUCTURE BOMs, fetch mounting structure items separately
          // Fixed By System on 06-Feb-2026 - Fetch parts from mounting_structure_parts table
          let mountingItemsList = [];
          if (bomType === 'MOUNTING_STRUCTURE') {
            try {
              const mountingItems = await solisPowerApi.fetchMountingStructuresByCategory(bom.id);

              // Fixed By System on 06-Feb-2026 - Fetch parts for each mounting structure
              if (mountingItems && Array.isArray(mountingItems) && mountingItems.length > 0) {
                const itemsWithParts = await Promise.all(
                  mountingItems.map(async (item) => {
                    try {
                      // Fetch parts for this mounting structure from mounting_structure_parts table
                      const parts = await solisPowerApi.fetchMountingStructurePartsByStructure(item.id);

                      // Convert parts from DB format to frontend format
                      // shape and make are stored as comma-separated strings, convert to arrays for display
                      const partsArray = (parts && Array.isArray(parts) ? parts : []).map(part => ({
                        component_name: part.component_name || '',
                        specs: part.specs || '',
                        size: part.size || '',
                        shape: part.shape ? (typeof part.shape === 'string' ? part.shape.split(',').filter(s => s.trim()) : []) : [],
                        make: part.make ? (typeof part.make === 'string' ? part.make.split(',').filter(m => m.trim()) : []) : [],
                        others: part.others || ''
                      }));

                      // Fixed By System on 06-Feb-2026 - Map DB field names to frontend field names for display
                      // Backend returns: specs, length, notes
                      // Frontend expects: special_structure_specs, special_structure_length, cleaning_system, others_text
                      const mappedItem = {
                        ...item,
                        parts: partsArray
                      };

                      // Map SPECIAL_STRUCTURE fields from DB format to frontend format
                      if (item.item_type === 'SPECIAL_STRUCTURE') {
                        // Map specs -> special_structure_specs
                        if (item.specs !== undefined && item.specs !== null) {
                          mappedItem.special_structure_specs = item.specs;
                        }
                        // Map length -> special_structure_length
                        if (item.length !== undefined && item.length !== null) {
                          mappedItem.special_structure_length = item.length;
                        }
                        // Map notes -> cleaning_system or others_text based on special_structure_type
                        if (item.notes !== undefined && item.notes !== null) {
                          if (item.special_structure_type === 'CLEANING SYSTEM') {
                            mappedItem.cleaning_system = item.notes;
                          } else if (item.special_structure_type === 'OTHERS') {
                            mappedItem.others_text = item.notes;
                          }
                        }
                      }

                      return mappedItem;
                    } catch (partError) {
                      console.error('[BOM View] Error fetching parts for structure', item.id, ':', partError);
                      return {
                        ...item,
                        parts: []
                      };
                    }
                  })
                );

                mountingItemsList = itemsWithParts;
              } else {
                mountingItemsList = [];
              }
            } catch (error) {
              console.warn('Error fetching mounting structure items:', error);
              // Fallback: check if items have mounting structure fields
              mountingItemsList = allItems.filter(item =>
                item.item_type === 'COMPONENT' ||
                item.item_type === 'COMBO_DISTRIBUTION' ||
                item.item_type === 'SPECIAL_STRUCTURE' ||
                item.roof_type ||
                item.component_name
              );
            }
          }

          setStandardItems([...standardItemsList].sort((a, b) => (a.s_no ?? 9999) - (b.s_no ?? 9999)));
          setMountingStructureItems([...mountingItemsList].sort((a, b) => (a.s_no ?? 9999) - (b.s_no ?? 9999)));

          // Set default tab based on BOM type
          if (bomType === 'MOUNTING_STRUCTURE' && mountingItemsList.length > 0) {
            setActiveTab('mounting');
          }
        } else {
          setBom({});
          setStandardItems([]);
          setMountingStructureItems([]);
        }
      } catch (error) {
        console.error("Error fetching BOM:", error);
        setBom({});
        setStandardItems([]);
        setMountingStructureItems([]);
      } finally {
        setLoading(false);
      }
    }

    initBom();
  };

  // Fixed By System on 06-Feb-2026 - Ensure correct BOM ID is passed for delete
  const deleteBom = async () => {
    // Get ID from multiple sources to ensure we have it
    const bomId = bom?.id || location?.state?.id || (location?.pathname?.split("/").length >= 3 ? location.pathname.split("/")[2] : null);

    if (!bomId) {
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Error",
        message: "BOM ID not found. Cannot delete.",
      });
      return;
    }

    const result = await solisPowerApi.deleteBomCategory(bomId);
    if (result && result.success !== false) {
      PubSub.publish("RECORD_SAVED_TOAST", {
        title: "Deleted",
        message: "Record Deleted successfully",
      });
      navigate(`/boms`);
    } else {
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Error",
        message: result?.errors || "Failed to delete BOM",
      });
    }
  };

  const editBom = () => {
    navigate(`/boms/${bom.id}/e`, { state: bom });
  };

  // Bill of Material Line Items - ONLY: S.No, Products, specification, category, size_range_capacity, make, unit, quantity, manufacturer_warranty
  const standardHeader = [];
  standardHeader.push(
    {
      title: "S.No",
      prop: "s_no",
      isFilterable: true,
      isSortable: true,
      cell: (row) => row.s_no ?? "-",
    },
    {
      title: "Products",
      prop: "product_name",
      isFilterable: true,
      isSortable: true,
      cell: (row) => row.product_name ?? row.name ?? "-",
    },
    {
      title: "Specification",
      prop: "specification",
      isFilterable: true,
      isSortable: true,
      cell: (row) => row.specification ?? "-",
    },
    {
      title: "Category",
      prop: "category",
      isFilterable: true,
      isSortable: true,
      cell: (row) => (row.category ? String(row.category).replace(/,/g, ", ") : "-"),
    },
    {
      title: "SIZE / RANGE / CAPACITY",
      prop: "size_range_capacity",
      isFilterable: true,
      isSortable: true,
      cell: (row) => row.size_range_capacity ?? "-",
    },
    {
      title: "Make",
      prop: "make",
      isFilterable: true,
      isSortable: true,
      cell: (row) => row.make ?? "-",
    },
    {
      title: "Unit",
      prop: "unit",
      isFilterable: true,
      isSortable: true,
      cell: (row) => row.unit ?? "-",
    },
    {
      title: "Quantity",
      prop: "quantity",
      isFilterable: true,
      isSortable: true,
      cell: (row) => row.quantity ?? "-",
    },
    {
      title: "Manufacturer Warranty",
      prop: "manufacturer_warranty",
      isFilterable: true,
      isSortable: true,
      cell: (row) => row.manufacturer_warranty ?? "-",
    },
    {
      title: "Brand",
      prop: "brand_name",
      isFilterable: true,
      isSortable: true,
      cell: (row) => row.brand_name ?? "-",
    }
  );

  // Table headers for MOUNTING_STRUCTURE Line Items
  const mountingHeader = [];
  mountingHeader.push(
    {
      title: "S.No",
      prop: "s_no",
      isFilterable: true,
      isSortable: true,
      cell: (row) => row.s_no ?? "-",
    },
    {
      title: "Item Name",
      prop: "name",
      isFilterable: true,
      isSortable: true,
    },
    {
      title: "Item Type",
      prop: "item_type",
      isFilterable: true,
      isSortable: true,
      cell: (row) => (
        <Badge bg="info">{row.item_type || 'COMPONENT'}</Badge>
      ),
    },
    {
      title: "Roof Type",
      prop: "roof_type",
      isFilterable: true,
      isSortable: true,
    },
    {
      title: "Structure Height (Front)",
      prop: "structure_height_front",
      isFilterable: true,
      isSortable: true,
      cell: (row) => row.structure_height_front ? `${row.structure_height_front} ft` : '-',
    },
    {
      title: "Structure Height (Back)",
      prop: "structure_height_back",
      isFilterable: true,
      isSortable: true,
      cell: (row) => row.structure_height_back ? `${row.structure_height_back} ft` : '-',
    },
    {
      title: "Component Name",
      prop: "component_name",
      isFilterable: true,
      isSortable: true,
    },
    {
      title: "Status",
      prop: "status",
      isFilterable: true,
      isSortable: true,
      cell: (row) => (
        <Badge bg={row.status === "Active" ? "success" : "secondary"}>
          {row.status}
        </Badge>
      ),
    }
  );

  const labels = {
    beforeSelect: " ",
  };

  if (loading) {
    return (
      <Main>
        <Container>
          <Row>
            <Col className="text-center mt-5">
              <Spinner animation="border" />
            </Col>
          </Row>
        </Container>
      </Main>
    );
  }

  if (!bom || !bom.id) {
    return (
      <Main>
        <Container>
          <Row className="view-form">
            <Col lg={11} className="pb-3 pt-2">
              <Link className="nav-link" to="/boms">
                Home <i className="fa-solid fa-chevron-right"></i>{" "}
                <div style={{ color: "#23468c", display: "inline" }}>
                  BOM
                </div>
              </Link>
            </Col>
            <Col></Col>
          </Row>
          <Row className="view-form gx-5 mx-auto">
            <Col lg={9}>
              <Row className="view-form-header bg-custom align-items-center">
                <Col lg={12} className="text-center py-4">
                  <h5>Record Not Found</h5>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </Main>
    );
  }

  return (
    <Main>
      <Container>
        {bom && (
          <>
            {modalShow && (
              <Confirm
                show={modalShow}
                onHide={() => setModalShow(false)}
                deleteProduct={deleteBom}
                title="Confirm delete?"
                message="You are going to delete the record. Are you sure?"
                table="BOM"
              />
            )}
            <Row className="view-form">
              <Col lg={11} className="pb-3 pt-2">
                <Link className="nav-link" to="/boms">
                  Home <i className="fa-solid fa-chevron-right"></i>{" "}
                  <div style={{ color: "#23468c", display: "inline" }}>
                    BOM
                  </div>
                </Link>
              </Col>
              <Col></Col>
            </Row>
            {/* Fixed By System on 06-Feb-2026 - Full-width responsive layout */}
            <Row className="view-form gx-3">
              <Col xs={12}>
                <Row className="view-form-header bg-custom align-items-center">
                  <Col xs={12} md={8}>
                    BOM
                    <h5>{bom.name}</h5>
                  </Col>
                  <Col xs={12} md={4} className="d-flex justify-content-end mt-2 mt-md-0">
                    <>
                      {[PERMISSIONS.MODIFY_ALL].some(permission => {
                        return userInfo?.permissions.map(p => p.name).includes(permission);
                      }) && (
                          <Button
                            size="sm"
                            className="btn-edit mx-2"
                            onClick={() => editBom(true)}
                          >
                            <i className="fa-regular fa-pen-to-square"></i>
                          </Button>
                        )}
                      {[PERMISSIONS.DELETE_ALL].some(permission => {
                        return userInfo?.permissions.map(p => p.name).includes(permission);
                      }) && (
                          <Button
                            className="btn-sm"
                            variant="danger"
                            onClick={() => setModalShow(true)}
                          >
                            <i className="fa fa-trash-o"></i>
                          </Button>
                        )}
                    </>
                  </Col>
                </Row>

                {/* Conditional rendering based on BOM type */}
                {(bom.bom_type === 'STANDARD' || !bom.bom_type) && (
                  <>
                    {/* Bill of Material View */}
                    <Row className="py-4 ibs-edit-form">
                      <Col xs={12} sm={6} md={6} lg={6}>
                        <label>BOM Name</label>
                        <span>{bom.name}</span>
                      </Col>
                      <Col xs={12} sm={6} md={6} lg={6}>
                        <label>Status</label>
                        <span>
                          <Badge bg={bom.status === "Active" ? "success" : "secondary"}>
                            {bom.status}
                          </Badge>
                        </span>
                      </Col>
                      <Col xs={12} sm={6} md={6} lg={6}>
                        <label>Created Date</label>
                        <span>
                          {formatDateForView(bom?.createddate || "")}
                        </span>
                      </Col>
                      <Col xs={12} sm={6} md={6} lg={6}>
                        <label>Last Modified Date</label>
                        <span>
                          {formatDateForView(bom?.lastmodifieddate || "")}
                        </span>
                      </Col>
                    </Row>
                  </>
                )}

                {/* Mounting Structure View */}
                {/* Fixed By System on 06-Feb-2026 - Use mountingStructureItems instead of items */}
                {bom.bom_type === 'MOUNTING_STRUCTURE' && (
                  <MountingStructureView bom={bom} items={mountingStructureItems} />
                )}

                {/* Fixed By System on 06-Feb-2026 - Tabs for Standard and Mounting Structure Line Items */}
                {/* Full-width layout for tables */}
                <Row className="view-form mt-4">
                  <Col xs={12}>
                    <Row className="view-form-header bg-custom align-items-center mb-3">
                      <Col xs={12}>
                        <h5>BOM Line Items</h5>
                      </Col>
                    </Row>

                    {/* Tabs for separating Standard and Mounting Structure items */}
                    <Tabs
                      activeKey={activeTab}
                      onSelect={(k) => setActiveTab(k)}
                      className="mb-3"
                    >
                      {/* Bill of Material Tab */}
                      <Tab eventKey="standard" title={`Bill of Material (${standardItems.length})`}>
                        <DatatableWrapper
                          body={standardItems}
                          headers={standardHeader}
                          paginationOptionsProps={{
                            initialState: {
                              rowsPerPage: 50,
                              options: [5, 10, 15, 20, 30, 50]
                            },
                          }}
                        >
                          <Row className="align-items-end my-2">
                            <Col lg={2} md={4} xl={2} sm={12}>
                              <Filter
                                classes={{
                                  inputGroup: "",
                                  input: "",
                                  clearButton: "btn-custom",
                                }}
                              />
                            </Col>
                            <Col md={4} xl={1} lg={1} sm={12}>
                              <PaginationOptions labels={labels} />
                            </Col>
                            <Col lg={3} md={5} sm={12} className="py-1">
                              {/* <div className="d-flex py-1 px-2 ">
                                <InfoPill
                                  left="Total"
                                  right={standardItems?.length}
                                  rightClasses="bg-custom text-white border-white fw-bold"
                                  leftClasses="bg-white  fw-bold "
                                />
                              </div> */}
                            </Col>
                          </Row>

                          <div className="table-responsive my-3">
                            <Table striped className="shadow rounded sm my-4">
                              <TableHeader classes={{ th: 'bg-custom text-white sticky-header' }} />
                              {
                                (standardItems && Array.isArray(standardItems) && standardItems.length > 0) ?
                                  <TableBody />
                                  :
                                  <tbody className="">
                                    <tr>
                                      <td colSpan={100} align="center">
                                        <div className="py-4">
                                          No Bill of Material items found.
                                        </div>
                                      </td>
                                    </tr>
                                  </tbody>
                              }
                            </Table>
                          </div>
                          <Pagination
                            classes={{
                              buttonGroup: "btn-custom",
                              button: "bg-custom border-white",
                            }}
                          />
                        </DatatableWrapper>
                      </Tab>

                      {/* Mounting Structure Tab */}
                      <Tab eventKey="mounting" title={`Mounting Structure (${mountingStructureItems.length})`}>
                        <DatatableWrapper
                          body={mountingStructureItems}
                          headers={mountingHeader}
                          paginationOptionsProps={{
                            initialState: {
                              rowsPerPage: 50,
                              options: [5, 10, 15, 20, 30, 50]
                            },
                          }}
                        >
                          <Row className="align-items-end my-2">
                            <Col xs={12} sm={6} md={4} lg={3}>
                              <Filter
                                classes={{
                                  inputGroup: "",
                                  input: "",
                                  clearButton: "btn-custom",
                                }}
                              />
                            </Col>
                            <Col xs={12} sm={6} md={4} lg={2}>
                              <PaginationOptions labels={labels} />
                            </Col>
                            <Col xs={12} sm={12} md={4} lg={2} className="py-1">
                              {/* <div className="d-flex py-1 px-2 ">
                                <InfoPill
                                  left="Total"
                                  right={mountingStructureItems?.length}
                                  rightClasses="bg-custom text-white border-white fw-bold"
                                  leftClasses="bg-white  fw-bold "
                                />
                              </div> */}
                            </Col>
                          </Row>

                          <div className="table-responsive my-3">
                            <Table striped className="shadow rounded sm my-4">
                              <TableHeader classes={{ th: 'bg-custom text-white sticky-header' }} />
                              {
                                (mountingStructureItems && Array.isArray(mountingStructureItems) && mountingStructureItems.length > 0) ?
                                  <TableBody />
                                  :
                                  <tbody className="">
                                    <tr>
                                      <td colSpan={100} align="center">
                                        <div className="py-4">
                                          No mounting structure items found.
                                        </div>
                                      </td>
                                    </tr>
                                  </tbody>
                              }
                            </Table>
                          </div>
                          <Pagination
                            classes={{
                              buttonGroup: "btn-custom",
                              button: "bg-custom border-white",
                            }}
                          />
                        </DatatableWrapper>
                      </Tab>
                    </Tabs>
                  </Col>
                </Row>
              </Col>
            </Row>
          </>
        )}
      </Container>
    </Main>
  );
};

export default BomView;

